import { gql, useMutation } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal, NumberField } from '../../../../3-pattern';
import { TranslatedString } from '../../../../../interfaces';
import { getBackendLanguage } from '../../../../../assets/i18n/config';
import {
  Box, Switch, TextField, TranslatableTextField,
} from '../../../..';

const UPDATE_SIGNER_TYPE = gql`
  mutation updateSignerType($input: UpdateSignerTypeInput!) {
    updateSignerType(input: $input) {
      signerType {
        id
      }
    }
  }
`;

export interface SignerTypeInput {
  key: string;
  name: TranslatedString;
  isPrimary: boolean;
  defaultSigningOrder: number;
}

export interface ExistingSignerType {
  id: string;
  key: string;
  translatedName: TranslatedString;
  isPrimary: boolean;
  defaultSigningOrder: number;
}

export const EditSignerType = ({
  afterUpdate,
  signerTypeData,
  handleClose,
}: {
  afterUpdate: () => void;
  signerTypeData: ExistingSignerType;
  handleClose: () => void;
}) => {
  const initialSignerType: SignerTypeInput = {
    key: signerTypeData.key,
    name: signerTypeData.translatedName,
    isPrimary: signerTypeData.isPrimary,
    defaultSigningOrder: signerTypeData.defaultSigningOrder ?? 1,
  };

  const { t } = useTranslation(['settings']);
  const [signerType, setSignerType] = useState<SignerTypeInput>(initialSignerType);

  const [updateSignerType, { loading }] = useMutation(UPDATE_SIGNER_TYPE, {
    variables: {
      input: {
        signerTypeId: signerTypeData.id,
        translatedName: {
          en: signerType.name.en,
          fr: signerType.name.fr,
        },
        isPrimary: signerType.isPrimary,
        defaultSigningOrder: signerType.defaultSigningOrder,
      },
    },
    onCompleted: () => setSignerType(initialSignerType),
  });

  const update = async () => {
    await updateSignerType();
    afterUpdate();
  };

  const disabled = !signerType.name.en;

  return (
    <FormModal
      open
      loading={loading}
      disabled={disabled}
      title={t('signerTypes.editSignerTypeModal.title')}
      formButton={t('shared:update')}
      onSubmit={update}
      handleClose={handleClose}
      maxWidth='xs'
    >
      <Box display='flex' flexDirection='column' gap={2}>
        <TextField
          fullWidth
          disabled
          label={t('signerTypes.editSignerTypeModal.key')}
          value={signerType.key}
          onChange={(e: any) => setSignerType({ ...signerType, key: e.target.value })}
          testId='edit-signer-type-key'
        />
        <TranslatableTextField
          fullWidth
          label={t('signerTypes.editSignerTypeModal.name')}
          value={signerType.name}
          onChange={(value) => setSignerType({ ...signerType, name: value })}
          fallbackLanguage={getBackendLanguage()}
          testId='edit-signer-type-name'
        />
        <Switch
          label={t('signerTypes.editSignerTypeModal.isPrimary')}
          checked={signerType.isPrimary}
          onChange={(e: any) => setSignerType({ ...signerType, isPrimary: e })}
          testId='edit-signer-type-is-primary'
        />
        <NumberField
          label={t('signerTypes.editSignerTypeModal.defaultSigningOrder')}
          number={signerType.defaultSigningOrder.toString()}
          setNumber={(value: string) => setSignerType({ ...signerType, defaultSigningOrder: parseInt(value, 10) })}
          testId='edit-signer-type-default-signing-order'
          decimalPlaces={0}
        />
      </Box>
    </FormModal>
  );
};
